import { c as _c } from "react/compiler-runtime";
import { formatProductAttributes } from "@osmix/catalog-core/attributes";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import { api, type Product } from "@/api";
import { BagIcon, FavoriteHeartIcon, StarIcon, XIcon } from "@/components/icons";
import { ColorSwatches } from "@/components/product/color-swatches";
import { Price } from "@/components/product/price";
import { ProductShopTab } from "@/components/product/product-shop-tab";
import { productHref } from "@/lib/product-link";
import { resolveSizePick, sizeAvailability, sizeChipLabel } from "@/lib/product-size";
import { SizeChip } from "@/components/product/size-chip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCart, useWishlist } from "@/store";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "@osmix/ui/dialog";
import { Skeleton } from "@osmix/ui/skeleton";
import { notifyAddedToCart } from "@/lib/cart-toast";
import { orderQty } from "@/lib/min-order";

// «Быстрый просмотр» — модалка карточки товара БЕЗ перехода на страницу товара
// (кадр 3926:19504). Слева фото + лента миниатюр, справа бренд с крестиком,
// тип товара, рейтинг, цена, размеры и «В корзину», табы. Данные тянем только
// когда модалка открыта.
export function QuickViewModal(t0) {
  const $ = _c(33);
  const {
    productId,
    open,
    onOpenChange
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  let t2;
  if ($[0] !== productId) {
    t1 = ["products", "get", productId];
    t2 = () => api.products.get(productId as string);
    $[0] = productId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = open && Boolean(productId);
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const product = useQuery(t4);
  let t5;
  if ($[7] !== product.data || $[8] !== t) {
    t5 = !product.data && <DialogClose className="absolute right-4 top-5 z-10 flex size-6 items-center justify-center rounded-[4px] text-[#1F1F1F] outline-none transition-colors hover:text-osmix-tangelo focus-visible:ring-2 focus-visible:ring-ds-focus" aria-label={t("common.close", {
      defaultValue: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C"
    })}><XIcon /></DialogClose>;
    $[7] = product.data;
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== product.data?.name || $[11] !== t) {
    t6 = product.data?.name ?? t("product.quickView", {
      defaultValue: "\u0411\u044B\u0441\u0442\u0440\u044B\u0439 \u043F\u0440\u043E\u0441\u043C\u043E\u0442\u0440"
    });
    $[10] = product.data?.name;
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <DialogTitle className="sr-only">{t6}</DialogTitle>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = product.data?.brand ?? "";
  let t9;
  if ($[15] !== t8) {
    t9 = <DialogDescription className="sr-only">{t8}</DialogDescription>;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== onOpenChange || $[18] !== open || $[19] !== product.data || $[20] !== product.error || $[21] !== product.isPending || $[22] !== t) {
    t10 = product.isPending && open ? <QuickViewSkeleton /> : product.error || !product.data ? <div className="grid flex-1 place-items-center p-6 text-center type-body2 text-osmix-text-2">{t("common.error", {
        defaultValue: "\u0427\u0442\u043E-\u0442\u043E \u043F\u043E\u0448\u043B\u043E \u043D\u0435 \u0442\u0430\u043A"
      })}</div> : <QuickViewBody key={product.data.id} product={product.data} onClose={() => onOpenChange(false)} />;
    $[17] = onOpenChange;
    $[18] = open;
    $[19] = product.data;
    $[20] = product.error;
    $[21] = product.isPending;
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t5 || $[26] !== t7 || $[27] !== t9) {
    t11 = <DialogContent showCloseButton={false} overlayClassName="bg-black/20 backdrop-blur-[1.5px]" className="flex max-h-[92dvh] w-[95vw] max-w-[975px] flex-col gap-0 overflow-y-auto rounded-[20px] border-0 bg-[#F8F8F8] p-4 shadow-none sm:max-w-[975px] md:h-[737px] md:max-h-[90vh] md:flex-row md:overflow-hidden md:py-5 md:pl-4 md:pr-4">{t5}{t7}{t9}{t10}</DialogContent>;
    $[24] = t10;
    $[25] = t5;
    $[26] = t7;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== onOpenChange || $[30] !== open || $[31] !== t11) {
    t12 = <Dialog open={open} onOpenChange={onOpenChange}>{t11}</Dialog>;
    $[29] = onOpenChange;
    $[30] = open;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
}
function QuickViewBody({
  product,
  onClose
}: {
  product: Product;
  onClose: () => void;
}) {
  const {
    t,
    i18n
  } = useTranslation();
  const navigate = useNavigate();
  const addToCart = useCart(s => s.add);
  const saved = useWishlist(s_0 => s_0.has(product.id));
  const toggleProduct = useWishlist(s_1 => s_1.toggleProduct);
  const setProductLikedOnServer = useWishlist(s_2 => s_2.setProductLikedOnServer);
  const shops = useQuery({
    queryKey: ["shops", "list"],
    queryFn: () => api.shops.list()
  });
  const shop = shops.data?.find(s_3 => s_3.id === product.shopId);

  // Сводка отзывов для строки рейтинга (★ 4.5 · N оценок).
  const reviewSummary = useQuery({
    queryKey: ["products", "reviews", product.id],
    queryFn: () => api.products.reviews(product.id)
  });
  const ratingAvg = reviewSummary.data?.summary.average ?? 0;
  const ratingCount = reviewSummary.data?.summary.count ?? 0;
  const [active, setActive] = useState(0);
  const sizes = product.sizes.filter(s_4 => s_4.trim() !== ""); // пустые размеры не плашки (Айталы 13.10)
  const [size, setSize] = useState<string>(sizes.length === 1 ? sizes[0] ?? "" : "");
  const [sizeHint, setSizeHint] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<"specs" | "description" | "shop">("specs");
  const variants = product.variants ?? [];
  const defaultVariant = variants.find(v => v.stock > 0) ?? variants[0];
  // Вариант под выбранный размер — как на странице товара (resolveSizePick);
  // пока размер не выбран, вариант по умолчанию нужен только ради фото и цены.
  const variant = (size ? resolveSizePick(variants, size, defaultVariant?.color) : undefined) ?? defaultVariant;
  const photos = variant?.photos.length ? variant.photos : product.photos.length > 0 ? product.photos : [""];
  const activePhoto = photos[Math.min(active, photos.length - 1)];
  const displayPrice = variant?.price ?? product.price;
  const displayOldPrice = variant?.oldPrice ?? product.oldPrice;
  const sizeStock = sizeAvailability(variants);
  const displayStock = size || variants.length === 0 ? variant?.stock ?? product.stock : product.stock;
  const soldOut = displayStock <= 0;
  // Размер обязателен, когда их больше одного: раньше вариант подставлялся
  // сам, и товар уходил в корзину в первом размере без выбора покупателя.
  const needsSize = sizes.length > 1 && !size;
  const productLink = productHref(product.id, variant?.id);
  const add = async () => {
    if (needsSize) {
      setSizeHint(true);
      return;
    }
    if (soldOut || busy) return;
    setBusy(true);
    try {
      await addToCart({
        productId: product.id,
        variantId: variant?.id,
        size: variant?.size ?? (size || sizes[0] || ""),
        color: variant?.color ?? product.color,
        qty: orderQty(product)
      });
      notifyAddedToCart(t, () => navigate("/cart"), {
        photo: photos[0] || product.photos[0],
        brand: product.brand,
        name: product.name
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  };
  const toggleSaved = () => {
    const chosenSize = size || undefined;
    const liked = toggleProduct(product.id, chosenSize);
    void setProductLikedOnServer(product.id, liked, chosenSize);
  };
  const attributes = formatProductAttributes(product.categoryAttributes, i18n.language);
  const hasCustomization = Boolean(product.customization?.enabled && product.customization.options?.length);
  const productType = hasCustomization ? t("product.customizationAvailable") : product.madeToOrder ? t("product.madeToOrderBadge") : "";
  const colorLabel = t("product.color");
  const specRows: [string, string][] = ([[t("product.productType", {
    defaultValue: "Тип товара"
  }), productType], ...(product.madeToOrderLeadTimeDays && product.madeToOrder ? [[t("product.productionTime"), t("product.productionDays", {
    count: product.madeToOrderLeadTimeDays
  })]] : []), [t("product.category"), product.category], [colorLabel, variant?.color ?? product.color], ...attributes, [t("product.article"), product.article]] as [string, string][]).filter(([, v_0]) => v_0);
  const seller = shop ?? (product.sellerInfo ? {
    id: product.sellerInfo.shopId,
    slug: product.sellerInfo.slug,
    name: product.sellerInfo.name,
    rating: product.sellerInfo.rating
  } : undefined);
  return <>
			{/* ЛЕВО: фото 525×697 r8 (кадр 3926:19504) + лента миниатюр. */}
			<div className="flex min-h-0 shrink-0 flex-col gap-3 md:w-[525px]">
				<div className="relative min-h-0 flex-1 overflow-hidden rounded-[8px] bg-osmix-gray-100 max-md:aspect-[525/697]">
					{activePhoto ? <img src={activePhoto} alt={product.name} className="size-full object-contain" /> : null}
					{/* «Избранное на фото» 32 (DS), в углу фото с отступом 16. */}
					<button type="button" aria-pressed={saved} aria-label={saved ? t("wishlist.remove", {
          defaultValue: "Убрать из избранного"
        }) : t("wishlist.add", {
          defaultValue: "В избранное"
        })} onClick={toggleSaved} className="absolute right-4 top-4 flex size-8 items-center justify-center rounded-full outline-none transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-ds-focus">
						<FavoriteHeartIcon active={saved} />
					</button>
				</div>
				{photos.length > 1 && <div className="flex shrink-0 gap-2.5 overflow-x-auto pb-1">
						{photos.map((src, i) => <button key={`${src}-${i}`} type="button" onClick={() => setActive(i)} aria-label={t("product.viewImage", {
          defaultValue: "Показать изображение {{n}}",
          n: i + 1
        })} aria-current={i === active} className={cn("h-[100px] w-[74px] shrink-0 overflow-hidden rounded-[8px] outline-none transition-opacity focus-visible:ring-2 focus-visible:ring-ds-focus",
        // Лента миниатюр: 74×100 r8, активная без вуали,
        // неактивные под белой вуалью 50 %.
        i !== active && "opacity-50 hover:opacity-100")}>
								<img src={src} alt="" className="size-full bg-osmix-gray-100 object-cover" />
							</button>)}
					</div>}
			</div>

			{/* ПРАВО (кадр 3926:19504): колонка 398, блоки через 12 — бренд 26/30
       w700 с крестиком 24, тип товара 16/20, рейтинг, цена, табы. */}
			<div className="flex min-w-0 flex-1 flex-col gap-3 overflow-y-auto pt-4 md:max-w-[398px] md:pl-5 md:pt-0">
				<div className="flex items-start justify-between gap-3">
					<h2 className="min-w-0 text-[26px] font-bold leading-[30px] tracking-[-0.31px] text-[#161616]">
						<Link to={productLink} onClick={onClose} className="outline-none transition-colors hover:text-osmix-tangelo focus-visible:text-osmix-tangelo">
							{product.brand}
						</Link>
					</h2>
					<DialogClose className="mt-[3px] flex size-6 shrink-0 items-center justify-center rounded-[4px] text-[#1F1F1F] outline-none transition-colors hover:text-osmix-tangelo focus-visible:ring-2 focus-visible:ring-ds-focus" aria-label={t("common.close", {
          defaultValue: "Закрыть"
        })}>
						<XIcon />
					</DialogClose>
				</div>
				<p className="text-[16px] leading-5 text-[#2E2E2E]">{product.name}</p>

				{ratingCount > 0 && <div className="flex items-center gap-2.5">
						<span className="flex items-center gap-1.5">
							<StarIcon size={18} className="text-osmix-tangelo" />
							<span className="text-[16px] font-semibold leading-5 tracking-[-0.31px] text-[#161616]">{ratingAvg.toFixed(1)}</span>
						</span>
						<span className="text-[14px] font-medium leading-[19px] tracking-[-0.28px] text-[#1F1F1F]">
							{t("product.ratingsCount", {
            count: ratingCount
          })}
						</span>
					</div>}

				{/* Цена как на странице товара: тёмная, бейдж 37×17 pill. */}
				<Price value={displayPrice} oldValue={displayOldPrice} size="lg" pill="dark" />

				{/* Размеры и «В корзину» в кадре быстрого просмотра не нарисованы —
        оставлены по функции (вопрос дизайнеру в реестре). Плитки — те же,
        что на странице товара («42 / XS», «бабочка»), белые на подложке
        #F8F8F8. Единственный размер выбран сразу и не снимается (п. 12):
        быстрый просмотр открывают из идеи. */}
				{sizes.length > 0 && <div className="flex flex-wrap gap-2.5 pt-1" data-testid="quick-view-sizes">
						{sizes.map(s_5 => {
          const on = s_5 === size;
          return <SizeChip key={s_5} label={sizeChipLabel(s_5, variants)} selected={on} locked={sizes.length === 1} disabled={sizeStock.get(s_5) === 0} tone="white" onSelect={() => {
            setSize(on ? "" : s_5);
            setSizeHint(false);
            setActive(0);
          }} />;
        })}
					</div>}

				<div className="flex flex-col gap-2">
					<div className="flex flex-wrap items-center gap-x-5 gap-y-2">
						<Button onClick={add} disabled={soldOut || busy} data-testid="quick-view-add" className="min-w-[173px]">
							<BagIcon size={20} />
							<span>{busy ? t("product.adding") : soldOut ? t("product.outOfStock") : t("product.addToCart")}</span>
						</Button>
						<Button asChild variant="ghost" className="px-3">
							<Link to={productLink} onClick={onClose}>
								{t("product.openFull", {
                defaultValue: "Открыть товар"
              })}
							</Link>
						</Button>
					</div>
					{sizeHint && needsSize && <p role="alert" className="text-[14px] leading-[18px] text-ds-error" data-testid="quick-view-size-hint">
							{t("product.selectSize")}
						</p>}
				</div>

				{/* Табы Характеристики / Описание / О магазине: база 398×1 #1F1F1F 8 %,
        16/21, активная w600 с линией, остальные w500 48 %. */}
				<div className="flex flex-col gap-3 pt-1">
					<div className="flex max-w-[398px] gap-6 border-b border-[#1F1F1F]/8" role="tablist">
						{([["specs", t("product.attributes")], ["description", t("product.description")], ["shop", t("product.aboutShop")]] as const).map(([key, label]) => <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)} className={cn("relative h-8 shrink-0 whitespace-nowrap pt-1.5 text-[16px] leading-[21px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ds-focus", tab === key ? "font-semibold text-[#1F1F1F] after:absolute after:inset-x-0 after:bottom-0 after:h-px after:rounded-full after:bg-[#1F1F1F]" : "font-medium text-[#1F1F1F]/48 hover:text-[#1F1F1F]")}>
								{label}
							</button>)}
					</div>

					{tab === "specs" && (specRows.length > 0 ?
        // Строки как в кадре: ключ 133 w600, зазор 16, значение w500, #161616 14/18, шаг 30.
        <dl className="grid grid-cols-[minmax(0,45%)_minmax(0,1fr)] gap-x-4 sm:grid-cols-[minmax(133px,max-content)_minmax(0,1fr)] gap-y-3 text-[14px] leading-[18px] text-[#161616]">
								{specRows.map(([key_0, value]) => <div key={key_0} className="contents">
										<dt className="font-semibold sm:whitespace-nowrap">{key_0}</dt>
										<dd className="flex min-w-0 items-center gap-1.5 font-medium">
											{key_0 === colorLabel && <ColorSwatches colors={[value]} />}
											<span className="min-w-0 break-words">{value}</span>
										</dd>
									</div>)}
							</dl> : <p className="type-body2 text-osmix-text-2">{product.description}</p>)}

					{tab === "description" && <p className="whitespace-pre-line text-[14px] leading-[18px] text-[#161616]">{product.description}</p>}

					{tab === "shop" && seller && <ProductShopTab shop={{
          ...seller,
          logoUrl: shop?.logoUrl
        }} category={product.categoryId ? {
          id: product.categoryId,
          name: product.category
        } : undefined} onMessage={() => {
          onClose();
          navigate(`/chats?product=${encodeURIComponent(product.id)}`);
        }} />}
				</div>
			</div>
		</>;
}
function QuickViewSkeleton() {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="min-h-0 flex-1 rounded-[8px]" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex min-h-0 shrink-0 flex-col gap-3 md:w-[525px]">{t0}<div className="flex shrink-0 gap-3">{[0, 1, 2, 3].map(_temp)}</div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Skeleton className="h-8 w-40" />;
    t3 = <Skeleton className="h-5 w-28" />;
    t4 = <Skeleton className="h-5 w-24" />;
    t5 = <Skeleton className="h-7 w-32" />;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <>{t1}<div className="flex min-w-0 flex-1 flex-col gap-4 pt-4 md:pl-5 md:pt-1">{t2}{t3}{t4}{t5}<div className="flex gap-2">{[0, 1, 2].map(_temp2)}</div><Skeleton className="h-[39px] w-[136px] rounded-[8px]" /></div></>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
}

// Прокрутка активного фото на первое при смене товара обрабатывается через
// `key` на QuickViewBody (перемонтирование сбрасывает локальное состояние).
function _temp2(i_0) {
  return <Skeleton key={i_0} className="h-[39px] w-[52px] rounded-[10px]" />;
}
function _temp(i) {
  return <Skeleton key={i} className="h-[100px] w-[74px] rounded-[8px]" />;
}
export default QuickViewModal;